/**
 * Global Analytics Client
 *
 * Allows non-React code (like stores, API services, etc.) to call `track(event)`
 * even though the actual analytics implementation lives inside AnalyticsProvider.
 *
 * The provider calls `setTracker()` on mount to register its own `track` function.
 */

export type AnalyticsEvent = {
    eventType: string;
    resourceType?: string;
    resourceId?: string;
    userId?: string;
    metadata?: Record<string, any>;
};

// eslint-disable-next-line no-unused-vars
export type AnalyticsFn = (event: AnalyticsEvent) => void;

// Default tracker: no-op (does nothing until set by the provider)
// eslint-disable-next-line @typescript-eslint/no-empty-function
let tracker: AnalyticsFn = () => {};

/**
 * Used by the AnalyticsProvider to register its `track` implementation.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function setTracker(fn: AnalyticsFn) {
    tracker = fn;
}

/**
 * Can be called anywhere in the app — even outside React.
 * If the tracker isn’t set yet, it simply no-ops.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function track(event: AnalyticsEvent) {
    try {
        tracker(event);
    } catch (err) {
        if (process.env.NODE_ENV !== "production") {
            console.warn("Analytics track() call failed", err, event);
        }
    }
}
